// "Do [verb] to [value]", as many rows as you like, producing the JSON underneath.
//
// This component holds NO action vocabulary. Every verb and every closed value list
// arrives in `vocabulary`, served on the macros descriptor from the same arrays
// src/services/admin/macros.js validates against, so a dropdown cannot offer a value
// the gate rejects. All the decisions live in ../../pages/admin/macro-actions.js and
// are tested under node --test; what is left here is markup and two fetches.
//
// THE TWO OPEN LISTS ARE HTTP READS AND ONE RULE DECIDES EVERY CASE: a row's stored
// value is never cleared because a list did not arrive. While loading, the control
// is a disabled select showing the stored value as itself. If the read fails, it
// degrades to a text input pre-filled with that value — Save is NOT blocked, because
// one flaky GET must not make a macro uneditable when the server validates the value
// regardless.

import { useEffect, useRef, useState } from 'react';

import { apiFetch } from '../../api/client.js';
import {
  MAX_ACTION_ROWS, actionsToRows, rowsToActions, newRow, retypeRow, canAddRow,
  valueControlFor, withStoredOption, rowProblems, humanize,
} from '../../pages/admin/macro-actions.js';

/**
 * Where each value source is read from, and how a row becomes an option.
 *
 * A MAP rather than the ternary this used to be. A source with no entry fell through
 * to the tag list, so adding `queues` to any vocabulary would have filled a queue
 * dropdown with tag names and saved whichever one an admin clicked — a wrong value in
 * the one control that exists to prevent wrong values.
 *
 * `?can_be_assigned=true` is the right gate for a macro's assign and
 * `src/routing/assign.js`'s eligibility CTE is not: the write path's `assertAssignable`
 * checks exactly `can_be_assigned` and `is_active`, while the CTE adds queue membership
 * and an on-shift test. A macro assign is an explicit manual assignment, and the CTE
 * would hide targets the server accepts.
 */
const SOURCE_PATHS = {
  users: '/users?can_be_assigned=true',
  tags: '/tags',
  queues: '/queues',
  forms: '/ticket-forms',
  statuses: '/ticket-statuses',
  custom_fields: '/ticket-forms',
};

const SOURCE_OPTION = {
  users: (row) => ({ value: String(row.id), label: row.display_name }),
  tags: (row) => ({ value: row.name, label: row.name }),
  // Queues are chosen BY ID: `parseActions` tests `/^\d+$/` on `queue_id` and refuses
  // anything else, so a name here would store a rule that matches and routes nowhere.
  queues: (row) => ({ value: String(row.id), label: row.name }),
  forms: (row) => ({ value: String(row.id), label: row.name }),
  // A named status is chosen by id; the label carries the base status it rolls up to,
  // because "Escalate to L2" and "Start Hardware Process" are both `open` and an admin
  // picking between them needs to know that.
  statuses: (row) => ({
    value: String(row.id),
    label: row.base_status ? `${row.name} (${row.base_status})` : row.name,
  }),
};

/** The open lists, fetched once per screen. `status` is per source, so a failed tag
 *  read does not degrade the user dropdown beside it. */
function useValueSources(vocabulary) {
  const [sources, setSources] = useState({});
  const [attempt, setAttempt] = useState(0);

  const needed = Object.values(vocabulary ?? {})
    .filter((spec) => spec && !Array.isArray(spec) && typeof spec.source === 'string')
    .map((spec) => spec.source);
  const key = [...new Set(needed)].sort().join(',');

  useEffect(() => {
    if (key === '') return undefined;
    const controller = new AbortController();
    const wanted = key.split(',');
    setSources(Object.fromEntries(wanted.map((s) => [s, { status: 'loading', options: [] }])));

    for (const source of wanted) {
      const path = SOURCE_PATHS[source];
      // A source with no path here would previously have fallen through to /tags and
      // populated a queue dropdown with tag names — wrong values, silently, in a
      // control whose whole job is to stop a value the server refuses. Skip instead:
      // the control renders a text input when a source fails to load.
      if (!path) {
        setSources((prev) => ({ ...prev, [source]: { status: 'failed', options: [] } }));
        continue;
      }
      apiFetch(path, { signal: controller.signal })
        .then((res) => {
          const options = (res.data ?? []).map(SOURCE_OPTION[source]);
          setSources((prev) => ({ ...prev, [source]: { status: 'ready', options } }));
        })
        .catch((err) => {
          if (err?.name === 'AbortError') return;
          setSources((prev) => ({ ...prev, [source]: { status: 'failed', options: [] } }));
        });
    }
    return () => controller.abort();
  }, [key, attempt]);

  return [sources, () => setAttempt((n) => n + 1)];
}

function ValueControl({ row, control, sources, onChange, listId }) {
  const loaded = control.source ? sources[control.source] : { status: 'ready', options: control.options };
  const status = control.source ? (loaded?.status ?? 'loading') : 'ready';
  const options = withStoredOption(
    control.source ? (loaded?.options ?? []) : control.options, row.value, control,
  );

  if (control.kind === 'text' || status === 'failed') {
    return (
      <input
        type="text"
        className="field-input min-w-0 flex-1 py-1 text-sm"
        value={row.value}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }

  // A combobox, not a locked list: ticket writes create unknown tag names by name,
  // so a macro introducing a tag is a supported workflow — but tags cannot be
  // deleted and conditions match by name, so a typo is permanent and silent. The
  // datalist offers what exists while still letting a new name be typed.
  if (control.kind === 'combobox') {
    return (
      <>
        <input
          type="text"
          list={listId}
          className="field-input min-w-0 flex-1 py-1 text-sm"
          value={row.value}
          onChange={(event) => onChange(event.target.value)}
        />
        <datalist id={listId}>
          {options.map((option) => <option key={option.value} value={option.value} />)}
        </datalist>
      </>
    );
  }

  return (
    <select
      className="field-input min-w-0 flex-1 py-1 text-sm"
      value={row.value}
      disabled={status === 'loading'}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Choose…</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

export default function ActionBuilder({ id, value, vocabulary, onChange, onShowJson }) {
  // Rows are the state and the JSON is derived, the same way ActionsField's raw
  // text is the state. Seeded once from the loaded value: re-deriving on every
  // render would discard a half-filled row on each keystroke.
  const [rows, setRows] = useState(() => actionsToRows(value, vocabulary));
  const seq = useRef(0);
  const [sources, retry] = useValueSources(vocabulary);

  function commit(next) {
    setRows(next);
    onChange(rowsToActions(next));
  }

  const problems = rowProblems(rows, vocabulary);
  const failed = Object.entries(sources).filter(([, s]) => s.status === 'failed').map(([s]) => s);

  return (
    <div id={id} className="rounded-lg border border-line bg-surface p-3">
      {rows.length === 0 && (
        <p className="text-xs text-ink-muted">
          No actions. This macro only fills in the body.
        </p>
      )}

      <ul className="grid gap-2">
        {rows.map((row, index) => {
          if (row.kind === 'unknown') {
            // Kept, verbatim, locked, and re-emitted byte-identical on save. The
            // alternative every other actions reader in this codebase picks is to
            // drop it silently, which is the worst outcome available here.
            return (
              <li key={row.id} className="rounded border border-line-strong bg-sunken p-2">
                <div className="flex items-start justify-between gap-2">
                  <code className="font-mono text-xs break-all text-ink">
                    {JSON.stringify(row.raw)}
                  </code>
                  <button
                    type="button"
                    className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
                    onClick={() => commit(rows.filter((_, i) => i !== index))}
                  >
                    Remove
                  </button>
                </div>
                <p className="mt-1 text-xs text-ink-muted">
                  Not one of the actions this desk understands. It is kept exactly as it is;
                  editing it needs the JSON view.
                </p>
              </li>
            );
          }

          const control = valueControlFor(row.type, vocabulary);
          return (
            <li key={row.id} className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-ink-secondary">Do</span>
              <select
                className="field-input min-w-0 flex-1 py-1 text-sm"
                value={row.type}
                onChange={(event) => commit(
                  rows.map((r, i) => (i === index ? retypeRow(r, event.target.value) : r)),
                )}
              >
                {(vocabulary.types ?? []).map((type) => (
                  <option key={type} value={type}>{humanize(type)}</option>
                ))}
              </select>
              <span className="text-sm text-ink-secondary">to</span>
              <ValueControl
                row={row}
                control={control}
                sources={sources}
                listId={`${id}-${row.id}-list`}
                onChange={(next) => commit(
                  rows.map((r, i) => (i === index ? { ...r, value: next } : r)),
                )}
              />
              <button
                type="button"
                className="btn-ghost shrink-0 px-2 py-0.5 text-xs"
                onClick={() => commit(rows.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </li>
          );
        })}
      </ul>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className="btn-ghost ring-1 ring-line-strong px-2 py-0.5 text-xs disabled:opacity-50"
          disabled={!canAddRow(rows)}
          onClick={() => {
            seq.current += 1;
            commit([...rows, newRow(vocabulary, seq.current)]);
          }}
        >
          + Add action
        </button>
        <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={onShowJson}>
          Edit as JSON
        </button>
        {!canAddRow(rows) && (
          <span className="text-xs text-ink-muted">
            {MAX_ACTION_ROWS} is the most a macro can carry.
          </span>
        )}
      </div>

      {failed.length > 0 && (
        <p className="mt-2 text-xs text-ink-secondary">
          Couldn’t load the {failed.join(' and ')} list. Type the value instead.{' '}
          <button type="button" className="underline" onClick={retry}>Retry</button>
        </p>
      )}

      {problems.length > 0 && (
        <ul className="mt-2 grid gap-0.5">
          {problems.map((problem) => (
            <li key={problem.index} className="text-xs text-red-700 dark:text-red-300">
              {problem.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
